import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { observable, reaction, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import Bind from 'lodash-decorators/bind';

import messages from './SimpleCaseStudyView.intl';

import styles from './SimpleCaseStudy.module.scss';

import { LOAD_STATES, ANCHOR } from 'common/consts/common';

import { Container, Title } from 'common/components/Layout/Layout';
import PrimaryButtonComponent from 'common/components/PrimaryButtonComponent/PrimaryButtonComponent';
import CategoryList from 'common/components/CategoryList/CategoryList';
import CaseBig from './components/CaseBig/CaseBig';
import CaseSmall from './components/CaseSmall/CaseSmall';
import LanguageService from 'services/Language.service';
import SimpleCaseStudyStore from 'stores/SimpleCaseStudy.store';
import CompaniesStore from 'stores/Companies';
import Placeholder from 'common/components/Placeholder/Placeholder';
import ContentBar from 'common/components/Placeholder/components/ContentBar/ContentBar';
import PageNotFoundView from 'views/PageNotFound/PageNotFoundView';

import { isOnMobile } from 'utils/device-utils';
import SimpleCaseStudyPlaceholder from './placeholder/SimpleCaseStudyPlaceholder';

const TOP_CASESTUDY_LIMIT = 6;

@injectIntl
@withRouter
@observer
export default class SimpleCaseStudyView extends Component {
  criteria = observable({
    page: 1,
    category: this.props.match.params.category || ''
  });
  caseStudies = observable([]);
  loadState = observable.box('');
  isExploited = observable.box(false);
  companies = CompaniesStore.companies;
  currentLanguage = LanguageService.getLang();

  async componentDidMount() {
    reaction(() => this.criteria.category, () => this.caseStudies.clear());

    reaction(() => this.criteriaKey, () => this.getCaseStudies(this.criteria), {
      fireImmediately: true
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { category } = this.props.match.params;

    if (category !== prevProps.match.params.category) {
      this.updateCriteria({
        category: category || '',
        page: 1
      });
    }
  }

  @computed
  get criteriaKey() {
    return JSON.stringify(this.criteria);
  }

  @computed
  get isLoaded() {
    return this.loadState.get() === LOAD_STATES.LOADED;
  }

  @computed
  get latestCaseStudies() {
    return this.caseStudies.slice(0, TOP_CASESTUDY_LIMIT);
  }

  @computed
  get otherCaseStudy() {
    return this.caseStudies.slice(TOP_CASESTUDY_LIMIT);
  }

  @computed
  get categoryList() {
    const defaultOption = {
      text: this.props.intl.formatMessage(messages.taxonomyAll),
      value: ''
    };

    const companiesOptions = this.companies.map(c => ({
      text: c.name,
      value: c.codename
    }));

    return [defaultOption, ...companiesOptions];
  }

  @action
  updateCriteria(newCriteria) {
    Object.assign(this.criteria, newCriteria);
  }

  @action
  async getCaseStudies(criteria) {
    this.loadState.set(LOAD_STATES.LOADING);
    const { data, isExploited } = await SimpleCaseStudyStore.fetchCaseStudies(
      criteria
    );
    runInAction(() => {
      this.caseStudies.push(...data);
      this.loadState.set(LOAD_STATES.LOADED);
      this.isExploited.set(isExploited);
    });
  }

  @Bind()
  async showMore() {
    this.updateCriteria({
      page: this.criteria.page + 1
    });
  }

  @Bind()
  onCategoryChange(category) {
    const {
      history,
      match: {
        params: { lang }
      }
    } = this.props;

    const langPrefix = lang ? `/${lang}` : '';
    history.push(
      `${langPrefix}${ANCHOR.CASESTUDIES[this.currentLanguage]}/${category}`
    );
  }

  render() {
    const {
      criteria: { category },
      isExploited
    } = this;

    const isValidCategory =
      category === '' ||
      this.companies.find(
        company => company.slug === this.props.match.params.category
      );

    if (!isValidCategory && this.companies.length) {
      return <PageNotFoundView />;
    }

    return (
      <div>
        <Container>
          <Title title={this.props.intl.formatMessage(messages.heading)} />
        </Container>
        <Container className={styles.Container}>
          <div className={styles.CaseStudiesColumn}>
            <Placeholder
              component={<SimpleCaseStudyPlaceholder />}
              ready={this.isLoaded || !!this.caseStudies.length}
            >
              <h2 className={styles.LastAddedTitle}>
                {this.props.intl.formatMessage(messages.lastAdded)}
              </h2>
              {this.isLoaded &&
                !this.caseStudies.length && (
                  <p className={styles.NoResults}>
                    {this.props.intl.formatMessage(messages.nothingToShow)}
                  </p>
                )}
              <div className={styles.RecentCaseStudy}>
                {this.latestCaseStudies.map(casestudy => (
                  <CaseBig key={casestudy.id} caseStudy={casestudy} />
                ))}
              </div>
              {this.otherCaseStudy.length > 0 && (
                <div>
                  <h2 className={styles.LastAddedTitle}>
                    {this.props.intl.formatMessage(messages.earlier)}
                  </h2>
                  {this.otherCaseStudy.map(caseStudy => (
                    <CaseSmall
                      caseStudy={caseStudy}
                      key={caseStudy.id}
                      className={styles.OldCase}
                    />
                  ))}
                </div>
              )}
              {!isExploited.get() && (
                <div className={styles.PrimaryButtonStyle}>
                  <PrimaryButtonComponent
                    className={styles.PrimaryButton}
                    text={this.props.intl.formatMessage(messages.showMore)}
                    onClick={this.showMore}
                    isBusy={!this.isLoaded}
                  />
                </div>
              )}
            </Placeholder>
          </div>
          <div className={styles.SidebarCategories}>
            <h3 className={styles.CategoryTitle}>
              {this.props.intl.formatMessage(messages.categories)}
            </h3>
            <Placeholder
              component={
                <ContentBar className={styles.ContentBarPlaceholder} />
              }
              ready={this.categoryList.length > 1}
              repeat={isOnMobile() ? 1 : 6}
            >
              <CategoryList
                categories={this.categoryList}
                onSelect={this.onCategoryChange}
                activeOption={category}
              />
            </Placeholder>
          </div>
        </Container>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/SimpleCaseStudy/SimpleCaseStudy.js